<script lang="ts">
    import ButtonLink from '#cmp/shared/button/ButtonLink.svelte'
    import Icon from '#cmp/shared/layout/Icon.svelte'
    import MainPageLinkPreview from '#cmp/specific/landing/HeroLink.svelte'
    import FaGithub from '~icons/fa-brands/github'
    import FaBook from '~icons/fa-solid/book'
    import FaCode from '~icons/fa-solid/code'
    import FaArrowRight from '~icons/fa-solid/arrow-right'
    import FaGraduationCap from '~icons/fa-solid/graduation-cap'
    import MainPageSection from '#cmp/specific/landing/HeroSection.svelte'
    import AgentDocs from '#cmp/documentation/site/AgentDocs.svelte'
    import RgbLine from '#cmp/shared/misc/RgbLine.svelte'
    import GoLinkExternal from '~icons/octicon/link-external-16'
    import { ThemeStore } from '#stores/themeStore.svelte.ts'
    import { onMount } from 'svelte'
    import FaDownload from '~icons/fa-solid/download'
    import FaDonate from '~icons/fa-solid/heart'
    import Button from '#cmp/shared/button/Button.svelte'
    import Page from '#cmp/shared/layout/Page.svelte'
    import Row from '#cmp/shared/layout/Row.svelte'
    import Column from '#cmp/shared/layout/Column.svelte'
    import Card from '#cmp/shared/layout/Card.svelte'

    import { storedProjectCount } from '#lib/storage/projectCount.ts'
    import DefaultNavbar from '#cmp/shared/layout/DefaultNavbar.svelte'
    import FaDumbbell from '~icons/fa-solid/dumbbell'
    import SparklesIcon from '#cmp/shared/agent/SparklesIcon.svelte'
    import { resolve } from '$app/paths'
    import { AI_FEATURES_ENABLED } from '#lib/Config.ts'
    import { tooltip } from '#cmp/shared/floating/tooltip.svelte.ts'
    import { jsonLdScriptTag, softwareApplicationLd, websiteLd } from '#lib/seo.ts'

    interface BeforeInstallPromptEvent extends Event {
        prompt(): Promise<void>
    }

    const textShadowPrimary = ThemeStore.getColor('primary').isDark()
    const textShadowSecondary = ThemeStore.getColor('secondary').isDark()
    let installEvent: BeforeInstallPromptEvent | null = $state(null)
    onMount(() => {
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault()
            console.log('beforeinstallprompt', e)
            installEvent = e as BeforeInstallPromptEvent
        })
    })
    let shadow = $derived(textShadowPrimary && 'box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);')

    /**
     * How many Projects are stored, counted without reading them: `pending` until the count is
     * in, and undefined when the browser's storage could not be read.
     */
    let projectCount = $state<number | undefined | 'pending'>('pending')
    onMount(() => {
        void storedProjectCount().then((count) => (projectCount = count))
    })
    //a count that failed knows nothing of the projects, and their page says why
    let shouldGoToCurrentProjects = $derived(
        projectCount === 'pending' || projectCount === undefined || projectCount > 0
    )
</script>

<svelte:head>
    <title>ASM Editor: online assembler & emulator for M68K, MIPS, RISC-V, x86-64 and Z80</title>
    <meta
        name="description"
        content="Write, learn and run M68K, MIPS, RISC-V, x86-64 and Z80 assembly code in your browser. View registers and memory, step and undo the execution."
    />
    <meta
        property="og:description"
        content="Write, learn and run M68K, MIPS, RISC-V, x86-64 and Z80 assembly code in your browser. View registers and memory, step and undo the execution."
    />
    <meta
        property="og:title"
        content="ASM Editor: online assembler & emulator for M68K, MIPS, RISC-V, x86-64 and Z80"
    />
    <!-- The page's schema.org description. {@html} is the only way to emit a <script>
         from a component: <svelte:element this="script"> renders nothing in Svelte, and a
         literal script tag here is taken as the component's own instance script. Safe
         because the payload is machine generated and serializeJsonLd escapes < > and &,
         so nothing interpolated can close the tag. -->
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html jsonLdScriptTag(softwareApplicationLd())}
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html jsonLdScriptTag(websiteLd())}
</svelte:head>
<DefaultNavbar />
<Page>
    <div class="main">
        <div class="content row">
            <img
                src="/images/ASM-Pacman.webp"
                alt="Pac-Man written in C, running on RISC-V in ASM Editor"
                class="preview-image"
            />
            <div class="presentation">
                <h1 class="welcome-title" class:textShadow={textShadowPrimary}>
                    The best web IDE for Assembly <br />
                    <span style="font-size: 1.2rem; opacity: 0.8">
                        M68K, MIPS, RISC-V, x86-64, Z80
                    </span>
                </h1>

                <Column gap="0.6rem">
                    <Row gap="0.6rem" wrap>
                        <ButtonLink
                            style={`${shadow}; padding: 0.5rem 0.7rem`}
                            cssVar="tertiary"
                            href="https://github.com/Specy/asm-editor"
                            {@attach tooltip('Open the project on github')}
                            aria-label="GitHub"
                        >
                            <Icon>
                                <FaGithub />
                            </Icon>
                        </ButtonLink>
                        <ButtonLink
                            style={`${shadow}; padding: 0.5rem 0.7rem`}
                            cssVar="tertiary"
                            href="/donate"
                            {@attach tooltip('Donate to the project')}
                            aria-label="Donate"
                        >
                            <Icon>
                                <FaDonate />
                            </Icon>
                        </ButtonLink>
                        <ButtonLink
                            href={shouldGoToCurrentProjects ? '/projects' : '/projects/create'}
                            style={`${shadow} gap: 0.5rem; min-width: 12.5rem`}
                            {@attach tooltip('Open the editor')}
                        >
                            {#if shouldGoToCurrentProjects}
                                Go to your projects
                            {:else}
                                Create first project
                            {/if}
                            <Icon>
                                <FaArrowRight />
                            </Icon>
                        </ButtonLink>
                    </Row>

                    <Row wrap gap="0.5rem">
                        <ButtonLink
                            href="/learn/courses"
                            cssVar="accent2"
                            style={`${shadow} gap: 0.5rem`}
                        >
                            Courses
                            <Icon>
                                <FaGraduationCap />
                            </Icon>
                        </ButtonLink>
                        <Button
                            style={`${shadow} gap: 0.5rem; min-width: 11.25rem; ${!installEvent ? 'pointer-events: none; opacity: 0' : ''}`}
                            cssVar="secondary"
                            onClick={async () => {
                                try {
                                    await installEvent?.prompt()
                                } catch (e) {
                                    console.error(e)
                                }
                                installEvent = null
                            }}
                        >
                            <Icon>
                                <FaDownload />
                            </Icon>
                            Install WebApp
                        </Button>
                    </Row>
                </Column>
            </div>
        </div>
        <RgbLine animated height="0.5rem" style="border-radius: 0;" />
        <div class="links-row-wrapper">
            <div class="links-row">
                <MainPageLinkPreview href={resolve('/documentation')}>
                    {#snippet icon()}
                        <div>
                            <FaBook />
                        </div>
                    {/snippet}
                    {#snippet description()}
                        Documentation
                    {/snippet}
                </MainPageLinkPreview>
                <MainPageLinkPreview href={resolve('/examples')}>
                    {#snippet icon()}
                        <div>
                            <FaCode />
                        </div>
                    {/snippet}
                    {#snippet description()}
                        Examples
                    {/snippet}
                </MainPageLinkPreview>
                <MainPageLinkPreview href={resolve('/learn/courses')}>
                    {#snippet icon()}
                        <div>
                            <FaGraduationCap />
                        </div>
                    {/snippet}
                    {#snippet description()}
                        Courses
                    {/snippet}
                </MainPageLinkPreview>
                <MainPageLinkPreview href={resolve('/donate')}>
                    {#snippet icon()}
                        <div>
                            <FaDonate />
                        </div>
                    {/snippet}
                    {#snippet description()}
                        Donate
                    {/snippet}
                </MainPageLinkPreview>
                <MainPageLinkPreview href={resolve('/embed')} title="Embed the app in your website">
                    {#snippet icon()}
                        <div>
                            <FaCode />
                        </div>
                    {/snippet}
                    {#snippet description()}
                        Embed
                    {/snippet}
                </MainPageLinkPreview>
                <MainPageLinkPreview href={resolve('/exam')} title="Create an exam for students">
                    {#snippet icon()}
                        <div>
                            <FaDumbbell />
                        </div>
                    {/snippet}
                    {#snippet description()}
                        Exam
                    {/snippet}
                </MainPageLinkPreview>
                {#if AI_FEATURES_ENABLED}
                    <MainPageLinkPreview href={resolve('/chat')}>
                        {#snippet icon()}
                            <div>
                                <SparklesIcon style="font-size: 1.8rem" />
                            </div>
                        {/snippet}
                        {#snippet description()}
                            AI Chat
                        {/snippet}
                    </MainPageLinkPreview>
                {/if}
            </div>
        </div>
    </div>
    <div class="column sections-wrapper">
        <MainPageSection id="documentation" imageUrl="/images/ASM-Documentation.webp">
            {#snippet title()}
                Documentation
            {/snippet}
            <div class="description" class:textShadow={textShadowPrimary}>
                The editor comes with built-in documentation for the M68K, MIPS, RISC-V, x86-64 and
                Z80 instruction sets including the valid addressing modes, description and examples
                for each instruction, directive and syscall.
                <a
                    href={resolve('/documentation')}
                    title="View documentation"
                    class="external-link"
                >
                    Visit the documentation
                    <div style="width: 1rem; height: 1rem; margin-top: 0.2rem; margin-left: 0.3rem">
                        <GoLinkExternal />
                    </div>
                </a>
            </div>
            <AgentDocs level={3} style="margin-top: 1.5rem;" />
        </MainPageSection>
        <MainPageSection id="codeCompletion" imageUrl="/images/ASM-CodeCompletion.webp" reverse>
            {#snippet title()}
                Code completion
            {/snippet}
            <div class="description" class:textShadow={textShadowSecondary}>
                Write and learn faster with the code completion tools, suggesting you with the valid
                addressing modes and shortcuts for labels and other keywords. Real time semantic
                errors that warn you before you compile the code.
            </div>
        </MainPageSection>
        <MainPageSection id="tools" imageUrl="/images/ASM-Tools.webp">
            {#snippet title()}
                Tools & Customisation
            {/snippet}
            <div class="description" class:textShadow={textShadowPrimary}>
                Feature rich tools to help you debug your code. Includes breakpoints, stepping,
                undo, poking registers and memory, stack tracer, register/memory diffing,
                decimal/hexadecimal conversions, stdout/stdin/errors, customisable shortcuts and
                settings, formatter and more. You can also customise the theme of the editor to your
                liking.
            </div>
        </MainPageSection>
        <MainPageSection id="compile" imageUrl="/images/ASM-Compile.webp" reverse>
            {#snippet title()}
                Compile from C/C++
            {/snippet}
            <div class="description" class:textShadow={textShadowSecondary}>
                Write C or C++ and compile it to MIPS, RISC-V or x86-64 assembly at any optimization
                level, with GCC, or Clang on MIPS and RISC-V. C source lines are mapped to the
                assembly instructions so you can see how loops, function calls and expressions
                become assembly.
            </div>
        </MainPageSection>
        <MainPageSection id="reverseEngineering" imageUrl="/images/ASM-ReverseEngineering.webp">
            {#snippet title()}
                Reverse engineering & CTFs
            {/snippet}
            <div class="description" class:textShadow={textShadowPrimary}>
                Import an x86-64 Linux ELF executable, to disassemble it, decompile it to C-like
                pseudocode with the Ghidra decompiler and edit its bytes in the hex editor. Rename
                functions, add comments, browse its strings and debug it with breakpoints, stepping
                and undo. Made for capture the flag challenges, cybersecurity courses and learning
                how compiled programs work.
                <a
                    href={resolve('/documentation/x86/executables')}
                    title="Read about Executables"
                    class="external-link"
                >
                    Read about Executables
                    <div style="width: 1rem; height: 1rem; margin-top: 0.2rem; margin-left: 0.3rem">
                        <GoLinkExternal />
                    </div>
                </a>
            </div>
        </MainPageSection>
        <MainPageSection id="peripherals" imageUrl="/images/ASM-Peripherals.webp" reverse>
            {#snippet title()}
                Peripherals
            {/snippet}
            <div class="description" class:textShadow={textShadowSecondary}>
                Programs can draw on a screen and interact with the user through mouse and keyboard
                or use the terminal for stdout and stdin. Available for M68K, MIPS, RISC-V and Z80.
                This can also be used to write simple interactive games and programs.
            </div>
        </MainPageSection>
        <MainPageSection id="exam">
            {#snippet title()}
                Exam
            {/snippet}
            <div class="description" class:textShadow={textShadowPrimary}>
                Create exams for students with the <a
                    href={resolve('/exam')}
                    title="Exam tool"
                    class="external-link"
                >
                    Exam tool
                    <div style="width: 1rem; height: 1rem; margin-top: 0.2rem; margin-left: 0.3rem">
                        <GoLinkExternal />
                    </div>
                </a>
                where the editor is locked down.
            </div>
        </MainPageSection>
        {#if AI_FEATURES_ENABLED}
            <MainPageSection id="chat" imageUrl="/images/ASM-AI-Chat.webp" reverse>
                {#snippet title()}
                    AI Chat
                {/snippet}
                <div class="description" class:textShadow={textShadowSecondary}>
                    Chat with an AI assistant that can write, run and debug assembly code directly
                    in the browser. Use the <a
                        href={resolve('/chat')}
                        title="AI Chat"
                        class="external-link"
                    >
                        AI Chat
                        <div
                            style="width: 1rem; height: 1rem; margin-top: 0.2rem; margin-left: 0.3rem"
                        >
                            <GoLinkExternal />
                        </div>
                    </a>
                    to get help with M68K, MIPS, RISC-V, x86-64 and Z80 assembly.
                </div>
            </MainPageSection>
        {/if}
        <!-- Without the AI Chat section, Embed follows Exam and takes the alternate side and colour. -->
        <MainPageSection id="embed" reverse={!AI_FEATURES_ENABLED}>
            {#snippet title()}
                Embed
            {/snippet}
            <div
                class="description"
                class:textShadow={AI_FEATURES_ENABLED ? textShadowPrimary : textShadowSecondary}
            >
                Embed the editor in your website to show and teach how assembly works using the <a
                    href={resolve('/embed')}
                    title="Embedder tool"
                    class="external-link"
                >
                    Embedder tool
                    <div style="width: 1rem; height: 1rem; margin-top: 0.2rem; margin-left: 0.3rem">
                        <GoLinkExternal />
                    </div>
                </a>
            </div>
        </MainPageSection>
        <section id="citation" class="citation">
            <Card
                background="secondary"
                border="tertiary"
                padding="2rem"
                gap="1rem"
                radius="0.8rem"
                align="start"
                style="width: 100%; max-width: 44rem"
            >
                <h2 class="citation-title">Published research</h2>
                <p>
                    ASM Editor is described in a peer-reviewed paper presented at the 2026 IEEE
                    Global Engineering Education Conference, together with the first-year course it
                    was built for.
                </p>
                <p class="reference">
                    E. Menichelli and L. Forlizzi, &ldquo;ASM Editor: Understanding Language
                    Abstractions Through Assembly Programming&rdquo;, 2026 IEEE Global Engineering
                    Education Conference (EDUCON), Cairo, Egypt, 2026.
                </p>
                <a
                    href="https://doi.org/10.1109/EDUCON67543.2026.11574463"
                    title="Read the EDUCON 2026 paper"
                    class="external-link"
                >
                    Read the paper
                    <div style="width: 1rem; height: 1rem; margin-top: 0.2rem; margin-left: 0.3rem">
                        <GoLinkExternal />
                    </div>
                </a>
            </Card>
        </section>
    </div>
</Page>

<div class="pre-footer" class:fade={false}></div>

<style lang="scss">
    /* Fades a last section on the secondary colour (a `reverse` one) into the footer. The last
       section is on the primary colour now, the footer's own, so `fade` is off in the markup: turn
       it back on if adding or removing a section leaves a `reverse` one last. */
    .pre-footer.fade {
        height: 5rem;
        min-height: 5rem;
        flex: 1;
        background: linear-gradient(0deg, var(--primary) 10%, var(--secondary));
    }

    .welcome-title {
        font-size: 3rem;
        padding: 1rem;
        max-width: 38rem;
        text-align: center;
        color: var(--primary-text);
        font-weight: bold;
        line-height: 1.15;
    }

    .textShadow {
        text-shadow: 2px 2px 12px rgb(36 36 36);
    }

    /* fades in over the section above it the way a MainPageSection does (HeroSection.svelte) */
    .citation {
        display: flex;
        justify-content: center;
        position: relative;
        z-index: 1;
        margin-top: -3rem;
        padding: 4.5rem 1rem 3rem;
        background-color: var(--primary);
        mask-image: linear-gradient(#0000, #000 3rem);
        font-size: 1.1rem;
        line-height: 1.6rem;
    }

    .citation-title {
        font-size: 2.2rem;
        font-weight: bold;
        line-height: 1.2;
    }

    .reference {
        font-size: 0.9rem;
        opacity: 0.8;
    }

    .sections-wrapper {
        padding: 3rem 0;
        padding-bottom: 0;
        font-family: 'Fira Code', monospace;
        background-color: var(--primary);
        color: var(--primary-text);
    }

    .presentation {
        display: flex;
        flex: 1;
        gap: 1rem;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background-color: rgba(var(--RGB-primary), 0.9);
        backdrop-filter: blur(2px);
        z-index: 2;
    }

    :global(body) {
        scroll-behavior: smooth;
    }

    .external-link {
        color: var(--accent);
        display: inline-flex;

        &:hover {
            text-decoration: underline;
        }
    }

    .links-row-wrapper {
        background-color: var(--secondary);
        color: var(--secondary-text);
        display: flex;
        justify-content: center;
    }

    .links-row {
        gap: 0.1rem;
        background-color: var(--background);
        display: flex;
        flex-wrap: wrap;
    }

    .main {
        display: flex;
        flex-direction: column;
        min-height: 100dvh;
        border-radius: 0.6rem;

        .content {
            display: flex;
            flex: 1;
            overflow: hidden;
            position: relative;
            border-radius: 0.45rem;
        }
    }

    .preview-image {
        display: flex;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        position: absolute;
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;
    }

    @media screen and (max-width: 650px) {
        .main {
            .content {
                width: 100%;
            }
        }
        .welcome-title {
            font-size: 2.3rem;
        }
    }

    @media screen and (max-width: 850px) {
        .citation {
            margin-top: 0;
            padding: 0.5rem 1rem 1.5rem;
            mask-image: none;
        }
        .citation-title {
            font-size: 1.8rem;
        }
        .pre-footer.fade {
            background: transparent;
        }
    }

    @keyframes gradient {
        0% {
            background-position: 0 50%;
        }
        50% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0 50%;
        }
    }
</style>
